import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Table } from "react-bootstrap";
import WagerModal from "../WagerModal";
import CashOutModal from "./CashOutModal";
import ActiveBetMatchExpand from "./ActiveBetMatchExpand";
import { getAllOrders } from "../../actions/gameSettings";
import { formatOdds, formatPoint, formatTotalPoint } from "../../../utility/odds";
import { useSelector, useDispatch } from "react-redux";
import { formatEventKickoff, formatPlacedAt } from "../../lib/dateFormat";
import Loader from "../Loader";
import BetStatusBadge from "../../common/BetStatusBadge";
import {
  areAllExpandableExpanded,
  getExpandableTicketIds,
  hasAnyExpandedBoards,
  isActiveBetExpandable,
  toggleExpandedTicketIds,
} from "../../../utility/activeBetGameLookup";
import { formatMoney } from "../../../utility/cashoutCalculation";
import "./cashOutModal.css";
import "./openbetExpand.css";
import { OPEN_BETS_REFRESH_EVENT } from "../../../context/BetSlipProvider";
import { isDTS } from "../../../utility/platform";
import {
  getTerm,
  getEntryTypeLabel,
  getFeaturedMarketLabel,
} from "../../../utility/complianceCopy";
import { formatWagerAmount, getWagerUnitName } from "../../../utility/wagerDisplay";
import {
  loadActiveEntriesFailedMessage,
  settlementActionLabel,
  settlementButtonAriaLabel,
} from "../../../utility/entryCopy";

const REFRESH_MS = 60 * 1000;

const Openbettable = () => {
  const accountData = useSelector((state) => state.account);
  const [show, setShow] = useState(false);
  const [cashOutBet, setCashOutBet] = useState(null);
  const [showCashOut, setShowCashOut] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [orderItem, setOrderItem] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedTicketIds, setExpandedTicketIds] = useState([]);
  const dispatch = useDispatch();
  const emailRef = useRef(accountData?.emailId);

  const expandableTicketIds = useMemo(
    () => getExpandableTicketIds(allOrders),
    [allOrders]
  );

  const allBoardsExpanded = useMemo(
    () => areAllExpandableExpanded(expandedTicketIds, allOrders),
    [expandedTicketIds, allOrders]
  );

  const anyBoardExpanded = hasAnyExpandedBoards(expandedTicketIds);

  useEffect(() => {
    emailRef.current = accountData?.emailId;
  }, [accountData?.emailId]);

  const handleClose = () => {
    setOrderItem({});
    setShow(false);
  };

  const handleShow = (data) => {
    setOrderItem(data);
    setShow(true);
  };

  const handleCashOutShow = (data) => {
    setCashOutBet(data);
    setShowCashOut(true);
  };

  const handleCashOutClose = () => {
    setCashOutBet(null);
    setShowCashOut(false);
  };

  const handleExpandToggle = (item) => {
    setExpandedTicketIds((current) =>
      toggleExpandedTicketIds(current, item?.ticketId)
    );
  };

  const handleExpandAllBoards = () => {
    setExpandedTicketIds(expandableTicketIds);
  };

  const handleCollapseAllBoards = () => {
    setExpandedTicketIds([]);
  };

  const handleExpandAllToggle = () => {
    if (allBoardsExpanded) {
      handleCollapseAllBoards();
    } else {
      handleExpandAllBoards();
    }
  };

  const isTicketExpanded = (ticketId) =>
    Boolean(ticketId) && expandedTicketIds.includes(ticketId);

  const renderLiveBoardButton = (item, { variant = "default" } = {}) => {
    if (!isActiveBetExpandable(item) || !item?.ticketId) return null;

    const isExpanded = isTicketExpanded(item.ticketId);
    const label = isExpanded ? "Hide Live Board" : "Live Board";

    return (
      <button
        type="button"
        className={[
          "openbet-live-board-btn",
          isExpanded ? "openbet-live-board-btn--active" : "",
          variant === "compact" ? "openbet-live-board-btn--compact" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => handleExpandToggle(item)}
        aria-expanded={isExpanded}
        aria-controls={`openbet-expand-${item.ticketId}`}
      >
        {label}
      </button>
    );
  };

  const fetchAllOrders = useCallback(async (opts = {}) => {
    const email = emailRef.current;
    if (!email) return;

    const silent = opts.silent === true;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setFetchError(null);

    try {
      const { status, result, message } = await getAllOrders(
        { email },
        dispatch
      );
      if (status === true && Array.isArray(result)) {
        const sorted = [...result].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setAllOrders(sorted);
        setLastUpdated(new Date());
      } else if (!silent) {
        setAllOrders([]);
        setFetchError(message || loadActiveEntriesFailedMessage());
      }
    } catch (err) {
      console.log("err", err);
      if (!silent) {
        setFetchError(loadActiveEntriesFailedMessage({ retry: true }));
      }
    } finally {
      if (!silent) setLoading(false);
      setRefreshing(false);
    }
  }, [dispatch]);

  useEffect(() => {
    const onOrdersRefresh = () => fetchAllOrders({ silent: true });
    window.addEventListener(OPEN_BETS_REFRESH_EVENT, onOrdersRefresh);
    return () =>
      window.removeEventListener(OPEN_BETS_REFRESH_EVENT, onOrdersRefresh);
  }, [fetchAllOrders]);

  useEffect(() => {
    fetchAllOrders();
    const id = setInterval(() => fetchAllOrders({ silent: true }), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchAllOrders, accountData?.emailId]);

  const renderRiskWin = (item) => {
    const risk = Number(item.wagerAmount || item.totalLossamount || 0);
    const payout = Number(item.payOut || item.totalWinamount || 0);
    const win = Math.max(0, payout - risk);
    if (isDTS()) {
      // Coins, never dollars, on DTS — in the coin the entry was funded with.
      const coins = (value) => formatWagerAmount(value.toFixed(2), item.fundingCoinBucket);
      return (
        <p className="mb-0">
          {coins(risk)} / {coins(win)}
        </p>
      );
    }
    return (
      <p className="mb-0">
        ${risk.toFixed(2)} / ${win.toFixed(2)}
      </p>
    );
  };

  const openBetRowKey = (item, index) => {
    const tid = item?.ticketId;
    if (tid !== undefined && tid !== null && String(tid).length > 0) {
      return `openbet-${tid}-${item?.betType ?? "bet"}`;
    }
    return `openbet-fallback-${index}`;
  };

  const getUniqueSports = (bets) => {
    const seen = new Set();
    return bets
      ?.map((b) => b.sport_title || b.title)
      .filter((sport) => {
        if (seen.has(sport)) return false;
        seen.add(sport);
        return true;
      });
  };

  const renderKickoffLine = (gameTime) => {
    const label = formatEventKickoff(gameTime, { includeTz: true });
    if (!label) return null;
    return (
      <>
        <br />
        {label}
      </>
    );
  };

  const formatLegLine = (bet) => {
    const market = bet?.oddType || bet?.type || "";
    if (market === "totals") {
      return `Total ${formatTotalPoint(bet.point, bet.pointType || bet.teamName)}`;
    }
    if (market === "spreads") {
      const line = bet.point ? formatPoint(bet.point) : "";
      return [bet.teamName, line ? `Spread ${line}` : "Spread"].filter(Boolean).join(" · ");
    }
    if (market === "h2h") {
      return [bet.teamName, getFeaturedMarketLabel("h2h", isDTS())]
        .filter(Boolean)
        .join(" · ");
    }
    return bet?.point ? `Line: ${bet.point}` : "";
  };

  const renderDescription = (item) => {
    const { betType } = item;
    if (betType === "Straight") {
      return (
        <div className="openbet-desc-cell">
          <div className="openbet-desc-cell__head">
            <p className="text-primary fw-bold mb-0">
              {getEntryTypeLabel("Straight", isDTS())}
            </p>
            {renderLiveBoardButton(item, { variant: "compact" })}
          </div>
          <div className="openbet-desc-cell__body">
            {item.homeTeam} vs {item.awayTeam}
            <br />
            {formatLegLine(item)}
            <br />
            Odds: {formatOdds(item.price)}
            {renderKickoffLine(item.gameTime)}
          </div>
        </div>
      );
    }
    if (betType === "Parlay") {
      return (
        <div className="openbet-desc-cell">
          <div className="openbet-desc-cell__head">
            <p className="text-primary fw-bold mb-0">
              {getEntryTypeLabel("Parlay", isDTS())}
            </p>
            {renderLiveBoardButton(item, { variant: "compact" })}
          </div>
          <div className="openbet-desc-cell__body">
            {item.bettingData?.map((bet, i) => (
              <p key={i} className="mb-2">
                {bet.homeTeam} vs {bet.awayTeam}
                <br />
                {formatLegLine(bet)}
                <br />
                Odds: {formatOdds(bet.price)}
                {renderKickoffLine(bet.gameTime)}
              </p>
            ))}
          </div>
        </div>
      );
    }
    if (betType === "Teaser") {
      return (
        <div className="openbet-desc-cell">
          <div className="openbet-desc-cell__head">
            <p className="text-primary fw-bold mb-0">
              {getEntryTypeLabel("Teaser", isDTS())}
            </p>
            {renderLiveBoardButton(item, { variant: "compact" })}
          </div>
          <div className="openbet-desc-cell__body">
            {item.bettingData?.map((bet, i) => (
              <p key={i} className="mb-2">
                {bet.homeTeam} vs {bet.awayTeam}
                <br />
                {bet.teamName}
                <br />
                Orig: {bet.originalPoint ? formatPoint(bet.originalPoint) : "-"} | Adj:{" "}
                {bet.point ? formatPoint(bet.point) : "-"}
                <br />
                Odds: {formatOdds(bet.price)}
                {renderKickoffLine(bet.gameTime)}
              </p>
            ))}
          </div>
        </div>
      );
    }
    if (betType) return <p className="mb-0">{getEntryTypeLabel(betType, isDTS())}</p>;
    return <p className="mb-0">{isDTS() ? getTerm("entry") : "Bet"}</p>;
  };

  const renderCashOutAction = (item) => {
    const cashoutEligibleType =
      item.betType === "Straight" || item.betType === "Parlay";
    if (!cashoutEligibleType || !item.cashoutAvailable) return null;

    const offer = Number(item.cashoutOffer);
    const offerLabel = Number.isFinite(offer) && offer > 0 ? formatMoney(offer) : null;
    const offerUnit = isDTS() ? getWagerUnitName(item.fundingCoinBucket) : null;
    const offerText = !offerLabel
      ? null
      : offerUnit
      ? `${offerLabel} ${offerUnit}`
      : `$${offerLabel}`;

    return (
      <div className="openbet-cashout">
        <button
          type="button"
          className="btn img_btn getbtn openbet-cashout__btn"
          onClick={() => handleCashOutShow(item)}
          aria-label={settlementButtonAriaLabel(item.ticketId, offerText)}
        >
          {settlementActionLabel()}
        </button>
        {offerLabel ? (
          <p
            className="openbet-cashout-amount"
            role="status"
            aria-label={`Amount ${offerText}`}
          >
            <span className="openbet-cashout-amount__value">
              {offerUnit ? offerLabel : `$${offerLabel}`}
            </span>
            {offerUnit ? (
              <span className="openbet-cashout-amount__unit">{offerUnit}</span>
            ) : null}
          </p>
        ) : null}
      </div>
    );
  };

  const renderSportCell = (item) => {
    if (item.betType === "Straight") {
      return <p className="mb-0">{item.sport_title || item.title}</p>;
    }
    const uniqueSports = getUniqueSports(item.bettingData);
    if (item.betType === "Teaser") {
      return <p className="mb-0">{uniqueSports?.join(", ")}</p>;
    }
    return uniqueSports?.map((sport, i) => (
      <p key={i} className="mb-0">
        {sport}
      </p>
    ));
  };

  const renderBetRow = (item, index) => {
    if (!["Straight", "Parlay", "Teaser"].includes(item.betType)) {
      return null;
    }

    const canExpand = isActiveBetExpandable(item);
    const isExpanded = canExpand && isTicketExpanded(item.ticketId);

    return (
      <tbody key={openBetRowKey(item, index)}>
        <tr className="match_collections">
          <td data-label="Ticket no">
            <Button
              onClick={() => handleShow(item)}
              className="open_wager_ticket_btn"
            >
              #{item.ticketId}
            </Button>
          </td>
          <td data-label="Date placed">
            <p className="mb-0">{formatPlacedAt(item.createdAt)}</p>
          </td>
          <td data-label="Sport">{renderSportCell(item)}</td>
          <td data-label="Description">{renderDescription(item)}</td>
          <td data-label="Status">
            <BetStatusBadge item={item} showDetail={true} />
          </td>
          <td data-label="Risk / Win">{renderRiskWin(item)}</td>
          <td data-label="Actions">
            <div className="openbet-actions">
              {renderLiveBoardButton(item)}
              {renderCashOutAction(item)}
              <button
                type="button"
                className="btn img_btn getbtn"
                onClick={() => handleShow(item)}
              >
                View
              </button>
            </div>
          </td>
        </tr>
        {isExpanded && (
          <tr className="openbet-expand-row" id={`openbet-expand-${item.ticketId}`}>
            <td colSpan={7}>
              <ActiveBetMatchExpand bet={item} />
            </td>
          </tr>
        )}
      </tbody>
    );
  };

  return (
    <section>
      <div className="dashboard_content_sec">
        <div className="teams_table_sec openbet">
          <div className="openbet-page-header mb-3">
            <div className="match_heading mb-0 openbet-page-header__title">
              {isDTS()
                ? `ACTIVE ${getTerm("entry", { plural: true, case: "upper" })}`
                : "ACTIVE BETS"}
            </div>
            <div className="openbet-page-header__actions">
              {lastUpdated ? (
                <span className="small text-muted openbet-page-header__updated">
                  Updated {formatPlacedAt(lastUpdated)}
                  {refreshing ? " · refreshing…" : ""}
                </span>
              ) : null}
              {expandableTicketIds.length > 0 ? (
                <button
                  type="button"
                  className="img_btn py-1 px-2 openbet-page-header__expand-all"
                  disabled={loading || refreshing}
                  onClick={handleExpandAllToggle}
                  aria-pressed={allBoardsExpanded}
                >
                  {allBoardsExpanded ? "Collapse All" : "Expand All"}
                </button>
              ) : null}
              {anyBoardExpanded && !allBoardsExpanded ? (
                <button
                  type="button"
                  className="img_btn py-1 px-2 openbet-page-header__collapse-all"
                  disabled={loading || refreshing}
                  onClick={handleCollapseAllBoards}
                >
                  Collapse All
                </button>
              ) : null}
              <button
                type="button"
                className="img_btn img_btn_xxl py-1 px-3 openbet-page-header__refresh"
                disabled={loading || refreshing}
                onClick={() => fetchAllOrders({ silent: true })}
              >
                Refresh
              </button>
            </div>
          </div>

          {loading && (
            <div className="py-5 d-flex justify-content-center">
              <Loader />
            </div>
          )}

          {!loading && fetchError && (
            <p className="text-danger text-center py-4 mb-0">{fetchError}</p>
          )}

          {!loading && !fetchError && (
            <Table responsive>
              {allOrders.length > 0 && (
                <thead>
                  <tr>
                    <th>TICKET NO</th>
                    <th>DATE PLACED</th>
                    <th className="sportTh">SPORT</th>
                    <th className="descTh">DESCRIPTION</th>
                    <th>STATUS</th>
                    <th>{isDTS() ? "RISK / WIN" : "RISK / WIN ($)"}</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
              )}

              {allOrders.length > 0 ? (
                allOrders.map((item, index) => renderBetRow(item, index))
              ) : (
                <tbody>
                  <tr>
                    <td colSpan={7} className="text-center">
                      {isDTS()
                        ? `NO ACTIVE ${getTerm("entry", { plural: true, case: "upper" })}`
                        : "NO ACTIVE BETS"}
                    </td>
                  </tr>
                </tbody>
              )}
            </Table>
          )}
        </div>
      </div>

      <WagerModal show={show} handleClose={handleClose} item={orderItem} />
      <CashOutModal
        show={showCashOut}
        onClose={handleCashOutClose}
        bet={cashOutBet}
        email={accountData?.emailId}
        onSuccess={() => fetchAllOrders({ silent: true })}
      />
    </section>
  );
};

export default Openbettable;
